import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  getCompanyName,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { ReportType } from "../utils/report";
import { OptionTypes } from "../../../components/shared/MultiSelect";
import { useEsopPlans } from "../../../queries";
import { useGetScheduledReports } from "../../../queries/scheduledReports";
import { PlanState } from "../../../types/EsopPlan";
import { generateGrantReport } from "../../../api/Esop";
import { ReportDto } from "../../../types/Grant";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../../utils/DownloadFile";
import { useGetOptionBuyback } from "../../../queries/optionBuyback";
import { EventType } from "../../../types/OptionBuyback";
import { HissaReportType, ScheduleParams } from "../../../types/common";
import ScheduleSection from "./ScheduleSection";
import { useGetAssociatedUsers } from "../../../queries/usersRoles";

const PoolStatementDialog = ({
  onClose = () => {},
  data,
}: Readonly<{
  onClose: () => void;
  data: any;
}>) => {
  const { data: _plans } = useEsopPlans();
  const plans = _plans || [];
  const planOptions: OptionTypes[] = [];
  const ids: string[] = [];
  const [shouldPiiBeMasked, setShouldPiiBeMasked] = useState(false);
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);
  const { data: associatedUsers } = useGetAssociatedUsers();
  const { data: scheduledReports = [], refetch: refetchScheduledReports } =
    useGetScheduledReports();
  const existingSchedule =
    data?.reportType &&
    scheduledReports.find(
      (s) => s.reportType === data.reportType && s.isActive
    );

  plans.forEach((plan) => {
    planOptions.push({ value: plan.planName });
    ids.push(plan.esopPlanId);
  });

  const initialValues: ReportType = {
    planIds: [],
    startDate: undefined,
    endDate: undefined,
  };
  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      values.maskPiiData = shouldPiiBeMasked;
      const payload =
        scheduleParams && scheduleParams.recipients.length > 0
          ? { ...values, scheduleParams }
          : values;
      data
        ?.onSubmitFunction(payload)
        .then((res: any) => {
          if (scheduleParams && scheduleParams.recipients.length > 0) {
            refetchScheduledReports();
          }
          toast(
            scheduleParams && scheduleParams.recipients.length > 0
              ? "Report scheduled successfully"
              : "Please wait while your report is being generated",
            { type: "info", autoClose: 2000 }
          );
          onClose();
        })
        .catch((err: any) => {
          toast(
            err?.response?.data?.errors || "Error while downloading the report",
            { type: "error", autoClose: 2000 }
          );
        });
    },
  });
  const fileName = `Pool Statement_${getCompanyName()}.xlsx`;
  async function downloadReport(reportDto: ReportDto) {
    onClose();
    const blob = await convertBase64ToBlob(
      reportDto.base64File,
      reportDto.fileType
    );
    downloadBlobObject(blob, fileName);
  }

  return (
    <div className="">
      <div className="p-8 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {data?.headerName}
          <span
            className="cursor-pointer"
            onClick={() => {
              onClose();
            }}
          >
            X
          </span>
        </h6>
      </div>
      <VStack className="w-full gap-2 px-8 py-4 ">
        {existingSchedule && (
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-md px-4 py-3 text-sm text-orange-800">
            <Icon
              icon="mdi:clock-alert-outline"
              width={16}
              className="mt-0.5 shrink-0 text-orange-500"
            />
            <span>
              This report is already scheduled (runs every{" "}
              <strong>{existingSchedule.intervalDays}</strong> day
              {existingSchedule.intervalDays !== 1 ? "s" : ""}). Submitting
              again will create an additional schedule.
            </span>
          </div>
        )}
        <HStack className="gap-8 ">
          <div className="flex-1">
            <Label className={"text-sm font-normal"}>Plan Names</Label>

            <MultiSelectV2
              _className="w-full"
              multiple={true}
              placeholder={"--Select plans--"}
              options={planOptions}
              handleSelection={(e) => {
                const planIds = plans
                  .filter((p) => e.includes(p.planName))
                  .map((p) => p.esopPlanId);
                formik.setFieldValue("planIds", planIds);
              }}
              optionValues={plans
                .filter((p) => formik.values.planIds?.includes(p.esopPlanId))
                .map((p) => p.planName)}
            />
          </div>
        </HStack>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>
        <HStack className="justify-end gap-2 mt-7">
          <input
            type="checkbox"
            checked={shouldPiiBeMasked}
            onChange={() => setShouldPiiBeMasked(!shouldPiiBeMasked)}
            className="text-white accent-orange-501"
            name=""
            id=""
          />
          <span>Hide all Personally Identifiable Information</span>
        </HStack>
        <HStack className="justify-end ">
          <ButtonPrimary
            onClick={() => {
              if (scheduleParams && scheduleParams.recipients.length === 0) {
                toast(
                  "Please select at least one recipient to schedule the report",
                  { type: "error", autoClose: 2000 }
                );
                return;
              }
              formik.handleSubmit();
            }}
          >
            {scheduleParams && scheduleParams.recipients.length > 0
              ? "Generate & Schedule"
              : "Generate"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
};

export default PoolStatementDialog;
